import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { User, Mail, Globe, Lock, Eye, EyeOff, ShieldCheck, Loader2, ArrowRight } from 'lucide-react';
import GoogleTranslate from '../components/GoogleTranslate';
import '../styles/App.css';

import BrandLogo from '../components/common/BrandLogo';
const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua & Deps","Argentina","Armenia","Australia",
  "Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin",
  "Bhutan","Bolivia","Bosnia Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina","Burma","Burundi",
  "Cambodia","Cameroon","Canada","Cape Verde","Central African Rep","Chad","Chile","China","Colombia","Comoros",
  "Congo","Congo (Democratic Rep)","Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti",
  "Dominica","Dominican Republic","East Timor","Ecuador","Egypt","El Salvador","Equatorial Guinea","Eritrea",
  "Estonia","Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia","Germany","Ghana","Greece","Grenada",
  "Guatemala","Guinea","Guinea-Bissau","Guyana","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran",
  "Iraq","Ireland","Israel","Italy","Ivory Coast","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati",
  "Korea North","Korea South","Kosovo","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya",
  "Liechtenstein","Lithuania","Luxembourg","Macedonia","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta",
  "Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia","Moldova","Monaco","Mongolia","Montenegro",
  "Morocco","Mozambique","Namibia","Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria",
  "Norway","Oman","Pakistan","Palau","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland",
  "Portugal","Qatar","Romania","Russian Federation","Rwanda","St Kitts & Nevis","St Lucia","Saint Vincent",
  "Samoa","San Marino","Sao Tome & Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone",
  "Singapore","Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Sudan","Spain","Sri Lanka",
  "Sudan","Suriname","Swaziland","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand",
  "Togo","Tonga","Trinidad & Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine",
  "United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City",
  "Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
];

function getPasswordStrength(password) {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (password.length >= 12) score++;
  if (score <= 1) return { score, label: 'Weak', color: '#ef4444' };
  if (score <= 2) return { score, label: 'Fair', color: '#f59e0b' };
  if (score <= 3) return { score, label: 'Good', color: '#3b82f6' };
  return { score, label: 'Strong', color: '#10b981' };
}

function Register() {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '',
    password: '', confirmPassword: '', country: '', referral: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleBlur = (e) => setTouched({ ...touched, [e.target.name]: true });

  const validateEmail = (email) => {
    return String(email).toLowerCase().match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const passwordsMismatch = formData.confirmPassword && formData.password !== formData.confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      firstName: true, lastName: true, email: true,
      country: true, password: true, confirmPassword: true
    });

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.country || !formData.password) {
      setError('Please fill in all mandatory fields.'); return;
    }
    if (!acceptedTerms) {
      setError('You must accept the Terms of Use to continue.'); return;
    }
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address.'); return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.'); return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters.'); return;
    }

    setLoading(true);
    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        country: formData.country,
        ...(formData.referral.trim() ? { referral: formData.referral.trim() } : {})
      };
      const res = await authAPI.createUser(payload);
      setSuccess(res.data?.message || 'Elite account created! Moving to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'System failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const iconStyle = (field) => ({
    position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)',
    color: touched[field] && !formData[field] ? '#ef4444' : 'var(--text-secondary)',
    transition: 'color 0.2s', zIndex: 2
  });

  const inputStyle = (field) => ({
    width: '100%', padding: '0.85rem 1rem 0.85rem 2.8rem',
    borderRadius: '12px', fontSize: '0.95rem',
    border: `1.5px solid ${touched[field] && !formData[field] ? '#ef4444' : 'var(--border-color)'}`,
    background: 'var(--bg-primary)', color: 'var(--text-primary)',
    transition: 'all 0.2s ease', outline: 'none',
    boxShadow: touched[field] && !formData[field] ? '0 0 0 4px rgba(239, 68, 68, 0.05)' : 'none'
  });

  const labelStyle = {
    display: 'block', fontSize: '0.85rem', fontWeight: '600',
    color: 'var(--text-secondary)', marginBottom: '0.5rem'
  };

  return (
    <div className="auth-split-wrapper">
      {/* Branding Side */}
      <div className="auth-brand-side">
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: 'url("/auth-bg-register.png")',
          backgroundSize: 'cover', backgroundPosition: 'center',
          animation: 'slowZoom 30s infinite alternate ease-in-out',
          zIndex: 1
        }} />
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.4))',
          zIndex: 2
        }} />
        
        <div style={{ position: 'relative', zIndex: 3, maxWidth: '600px', textAlign: 'left' }}>
          <div style={{ marginBottom: '2rem' }}>
            <BrandLogo style={{ fontSize: '2rem' }} />
          </div>
          <h1 style={{ 
            fontSize: '3.5rem', fontWeight: '900', color: 'white', 
            lineHeight: '1.1', marginBottom: '1.5rem', letterSpacing: '-0.02em'
          }}>
            Securing the Future of <span style={{ color: '#FCD535' }}>Quantitative Wealth.</span>
          </h1>
          <p style={{ 
            fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', 
            maxWidth: '500px', lineHeight: '1.6', fontWeight: '400' 
          }}>
            Join our elite algorithmic infrastructure and gain access to sophisticated capital management strategies designed for the digital age.
          </p>
          
          <div style={{ marginTop: '3rem', display: 'flex', gap: '2rem' }}>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white' }}>99.9%</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>Uptime</div>
            </div>
            <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)' }} />
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white' }}>AES-256</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>Encryption</div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Side */}
      <div className="auth-form-side" style={{ flex: 1.4 }}>
        <div className="auth-card glass" style={{ 
          maxWidth: '640px', width: '100%', padding: '3.5rem', 
          borderRadius: '32px', border: '1px solid var(--border-color)',
          background: 'var(--bg-modal)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
          animation: 'fadeInUp 0.5s ease-out'
        }}>
          <div style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ color: 'var(--text-primary)', fontSize: '2.2rem', fontWeight: '900', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>Join the Institution</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>Initialize your account and secure your portfolio today.</p>
          </div>

          {error && (
            <div style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444', padding: '1rem', borderRadius: '16px', marginBottom: '2rem', fontSize: '0.85rem', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
               <Lock size={16} /> {error}
            </div>
          )}
          {success && (
            <div style={{ backgroundColor: 'rgba(16,185,129,0.1)', color: '#10b981', padding: '1rem', borderRadius: '16px', marginBottom: '2rem', fontSize: '0.85rem', border: '1px solid rgba(16,185,129,0.2)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
               <ShieldCheck size={16} /> {success}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="auth-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div className="form-group">
                <label style={labelStyle}>First Name</label>
                <div style={{ position: 'relative' }}>
                  <span style={iconStyle('firstName')}><User size={18} /></span>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} onBlur={handleBlur} placeholder="John" style={inputStyle('firstName')} />
                </div>
              </div>
              <div className="form-group">
                <label style={labelStyle}>Last Name</label>
                <div style={{ position: 'relative' }}>
                  <span style={iconStyle('lastName')}><User size={18} /></span>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} onBlur={handleBlur} placeholder="Doe" style={inputStyle('lastName')} />
                </div>
              </div>
            </div>

            <div className="auth-grid-uneven" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div className="form-group">
                <label style={labelStyle}>Email Address</label>
                <div style={{ position: 'relative' }}>
                  <span style={iconStyle('email')}><Mail size={18} /></span>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleBlur} placeholder="john@example.com" style={inputStyle('email')} />
                </div>
              </div>
              <div className="form-group">
                <label style={labelStyle}>Country</label>
                <div style={{ position: 'relative' }}>
                  <span style={iconStyle('country')}><Globe size={18} /></span>
                  <select name="country" value={formData.country} onChange={handleChange} onBlur={handleBlur} style={{ ...inputStyle('country'), appearance: 'none', cursor: 'pointer' }}>
                    <option value="">Select...</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="auth-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div className="form-group">
                <label style={labelStyle}>Password</label>
                <div style={{ position: 'relative' }}>
                  <span style={iconStyle('password')}><Lock size={18} /></span>
                  <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} onBlur={handleBlur} placeholder="Password" style={{ ...inputStyle('password'), paddingRight: '2.5rem' }} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)' }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label style={labelStyle}>Confirm</label>
                <div style={{ position: 'relative' }}>
                  <span style={iconStyle('confirmPassword')}><Lock size={18} /></span>
                  <input type={showConfirm ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} onBlur={handleBlur} placeholder="Repeat" style={{ ...inputStyle('confirmPassword'), paddingRight: '2.5rem', border: `1.5px solid ${passwordsMismatch ? '#ef4444' : 'rgba(255,255,255,0.1)'}` }} />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)' }}>
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>

            {formData.password && (
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                  {[1,2,3,4].map(i => <div key={i} style={{ flex: 1, height: '4px', borderRadius: '2px', background: i <= passwordStrength.score ? passwordStrength.color : 'var(--border-color)', transition: 'background 0.3s' }} />)}
                </div>
                <div style={{ fontSize: '0.75rem', color: passwordStrength.color, fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{passwordStrength.label} Encryption Strength</div>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: '2rem' }}>
              <label style={{ ...labelStyle, display: 'flex', justifyContent: 'space-between' }}>
                Referral Code <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)', fontWeight: '400' }}>Optional</span>
              </label>
              <div style={{ position: 'relative' }}>
                <span style={iconStyle('referral')}><ShieldCheck size={18} /></span>
                <input type="text" name="referral" value={formData.referral} onChange={handleChange} placeholder="Invite code" style={inputStyle('referral')} />
              </div>
            </div>

            <div style={{ marginBottom: '2.5rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginTop: '3px' }}>
                 <input 
                   type="checkbox" 
                   id="terms"
                   checked={acceptedTerms}
                   onChange={(e) => setAcceptedTerms(e.target.checked)}
                   style={{ 
                     width: '20px', height: '20px', cursor: 'pointer',
                     accentColor: '#FCD535'
                   }} 
                 />
              </div>
              <label htmlFor="terms" style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', lineHeight: '1.6' }}>
                I acknowledge that I have read and agree to the <Link to="/terms" style={{ color: '#FCD535', fontWeight: '800', textDecoration: 'none' }}>Institutional Terms of Service</Link> and Privacy Protocols.
              </label>
            </div>

            <button type="submit" className="btn btn-primary" style={{ 
              width: '100%', padding: '1.25rem', borderRadius: '16px', fontSize: '1.1rem', 
              fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', 
              gap: '0.75rem', background: 'linear-gradient(135deg, #FCD535 0%, #d97706 100%)',
              color: '#000', border: 'none', boxShadow: '0 15px 30px rgba(245, 158, 11, 0.25)',
              cursor: loading || success || !acceptedTerms ? 'not-allowed' : 'pointer', 
              opacity: !acceptedTerms ? 0.6 : 1 
            }} disabled={loading || !!success || !acceptedTerms}>
               {loading ? <Loader2 size={22} className="spinner" /> : 'Establish Elite Account'}
            </button>
          </form>

          <div style={{ marginTop: '2.5rem', textAlign: 'center', fontSize: '1rem', color: 'var(--text-secondary)', opacity: 0.6 }}>
            Already registered? <Link to="/login" style={{ color: 'var(--accent-primary)', fontWeight: '800', textDecoration: 'none' }}>Sign In <ArrowRight size={16} style={{ verticalAlign: 'middle', marginLeft: '4px' }} /></Link>
          </div>
          <div style={{ marginTop: '2rem', opacity: 0.7 }}>
            <GoogleTranslate />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
